import { useState, useEffect, useRef } from "react";
import { motion, useScroll, AnimatePresence } from "framer-motion";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  Calendar,
  Clock,
  User,
  Tag,
  FolderOpen,
  Heart,
  Share2,
  BookOpen,
  ArrowLeft,
  Sparkles,
  Eye,
  Facebook,
  Twitter,
  Linkedin,
  Link as LinkIcon,
  Check,
  ChevronRight,
  MessageCircle,
  AlertCircle,
  Printer,
  Bookmark,
  Star,
  Flame,
  ThumbsUp,
  Handshake,
  Lightbulb,
  Smile,
  Loader2,
  Send,
  Reply,
  MoreVertical,
  Trash2,
  AlertOctagon,
  ChevronDown,
  MessageSquare,
} from "lucide-react";
import { blogService } from "../../api/services/blogService";
import { useSelector } from "react-redux";
import { format, isValid, parseISO } from "date-fns";
import { useToast } from "../../components/common/ThemeToaster";

// Blog Image Component
// Blog Image Component with proper BLOB handling
const BlogImage = ({ blog, className }) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [imageLoading, setImageLoading] = useState(true);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!isMounted.current) return;

    const processImage = () => {
      try {
        // Case 1: Backend already converted to base64 data URL
        if (blog?.featuredImage && typeof blog.featuredImage === "string") {
          setImageUrl(blog.featuredImage);
          setImageLoading(false);
          return;
        }

        // Case 2: Buffer object with data array (from JSON serialization)
        if (blog?.featuredImageBlob && blog.featuredImageBlob.data) {
          const bytes = new Uint8Array(blog.featuredImageBlob.data);
          let binary = "";
          const chunkSize = 8192;
          for (let i = 0; i < bytes.length; i += chunkSize) {
            binary += String.fromCharCode.apply(
              null,
              bytes.subarray(i, i + chunkSize),
            );
          }
          const base64 = btoa(binary);
          const mimeType = blog.featuredImageMimeType || "image/jpeg";
          setImageUrl(`data:${mimeType};base64,${base64}`);
          setImageLoading(false);
          return;
        }

        // Case 3: Buffer object with type field
        if (
          blog?.featuredImageBlob &&
          blog.featuredImageBlob.type === "Buffer"
        ) {
          const bytes = new Uint8Array(blog.featuredImageBlob.data || []);
          let binary = "";
          const chunkSize = 8192;
          for (let i = 0; i < bytes.length; i += chunkSize) {
            binary += String.fromCharCode.apply(
              null,
              bytes.subarray(i, i + chunkSize),
            );
          }
          const base64 = btoa(binary);
          const mimeType = blog.featuredImageMimeType || "image/jpeg";
          setImageUrl(`data:${mimeType};base64,${base64}`);
          setImageLoading(false);
          return;
        }

        // No image available
        setImageLoading(false);
      } catch (err) {
        console.error("Error processing blog image:", err);
        setImageLoading(false);
      }
    };

    processImage();
  }, [blog]);

  if (imageLoading) {
    return (
      <div className={`flex items-center justify-center ${className}`}>
        <Loader2 size={24} className="animate-spin text-primary-500" />
      </div>
    );
  }

  if (imageUrl) {
    return (
      <img
        src={imageUrl}
        alt={blog?.featuredImageAlt || blog?.title}
        className={`object-cover ${className}`}
        onError={() => {
          setImageUrl(null);
          setImageLoading(false);
        }}
      />
    );
  }

  return (
    <div className={`flex items-center justify-center ${className}`}>
      <BookOpen className="w-16 h-16 text-gray-400" />
    </div>
  );
};

// Reaction Button Component
const ReactionButton = ({ type, icon: Icon, count, isActive, onClick }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-1.5 px-2 py-1 text-xs rounded-lg transition-all ${
      isActive
        ? "bg-primary-500 text-white"
        : "text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
    }`}
  >
    <Icon className={`w-3.5 h-3.5 ${isActive ? "fill-white" : ""}`} />
    <span>{count}</span>
  </button>
);

// Comment Component
const Comment = ({
  comment,
  onReply,
  onLike,
  onDelete,
  currentUser,
  depth = 0,
}) => {
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [replyContent, setReplyContent] = useState("");
  const [isLiked, setIsLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(comment.likes || 0);
  const [showActions, setShowActions] = useState(false);
  const [isReplying, setIsReplying] = useState(false);
  const { success: toastSuccess, error: toastError } = useToast();
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleLike = async () => {
    if (isLiked) return;
    setIsLiked(true);
    setLikesCount((prev) => prev + 1);
    await onLike(comment.id);
    toastSuccess("Comment liked!");
  };

  const handleReplySubmit = async () => {
    if (!replyContent.trim()) return;
    setIsReplying(true);
    try {
      await onReply(comment.id, replyContent);
      if (!isMounted.current) return;
      setReplyContent("");
      setShowReplyForm(false);
      toastSuccess("Reply added successfully!");
    } catch (err) {
      if (isMounted.current) {
        toastError("Failed to add reply");
      }
    } finally {
      if (isMounted.current) {
        setIsReplying(false);
      }
    }
  };

  const handleDelete = async () => {
    if (window.confirm("Are you sure you want to delete this comment?")) {
      await onDelete(comment.id);
      toastSuccess("Comment deleted");
    }
  };

  const formatDate = (date) => {
    if (!date) return "Just now";
    try {
      const d = typeof date === "string" ? parseISO(date) : new Date(date);
      if (isValid(d)) {
        return format(d, "MMM dd, yyyy 'at' h:mm a");
      }
      return "Just now";
    } catch {
      return "Just now";
    }
  };

  const isAuthor = currentUser?.id === comment.userId;
  const isApproved = comment.status === "approved";
  const isPending = comment.status === "pending";
  const isSpam = comment.status === "spam";

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`relative ${depth > 0 ? "ml-6 sm:ml-12 mt-4" : "mt-4"}`}
    >
      <div
        className={`p-4 rounded-xl transition-all ${
          isPending
            ? "bg-yellow-50/50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-800"
            : isSpam
              ? "bg-red-50/50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 opacity-60"
              : "bg-white/40 dark:bg-gray-900/40 backdrop-blur-sm border border-white/30 dark:border-gray-800/30"
        }`}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3 flex-1">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center shadow-md flex-shrink-0">
              <span className="text-sm font-bold text-white">
                {comment.userName?.charAt(0) || "A"}
              </span>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className="font-semibold text-gray-900 dark:text-white">
                  {comment.userName || "Anonymous"}
                </span>
                {comment.userEmail && (
                  <span className="text-xs text-gray-500">
                    {comment.userEmail}
                  </span>
                )}
                {isPending && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-yellow-100 text-yellow-700">
                    <Clock className="w-3 h-3" />
                    Pending Review
                  </span>
                )}
                {isSpam && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700">
                    <AlertOctagon className="w-3 h-3" />
                    Flagged
                  </span>
                )}
              </div>
              <p className="text-gray-700 dark:text-gray-300 text-sm mb-2">
                {comment.content}
              </p>
              <div className="flex items-center gap-4 text-xs text-gray-500">
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {formatDate(comment.createdAt)}
                </span>
                <button
                  onClick={handleLike}
                  className={`flex items-center gap-1 hover:text-red-500 transition-colors ${isLiked ? "text-red-500" : ""}`}
                >
                  <Heart
                    className={`w-3 h-3 ${isLiked ? "fill-current" : ""}`}
                  />
                  {likesCount > 0 && <span>{likesCount}</span>}
                </button>
                {isApproved && (
                  <button
                    onClick={() => setShowReplyForm(!showReplyForm)}
                    className="flex items-center gap-1 hover:text-primary-500 transition-colors"
                  >
                    <Reply className="w-3 h-3" />
                    Reply
                  </button>
                )}
              </div>

              {/* Reply Form */}
              <AnimatePresence>
                {showReplyForm && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mt-4"
                  >
                    <div className="flex gap-3">
                      <textarea
                        value={replyContent}
                        onChange={(e) => setReplyContent(e.target.value)}
                        placeholder="Write your reply..."
                        rows={2}
                        className="flex-1 px-4 py-2 bg-white/50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm resize-none"
                      />
                      <button
                        onClick={handleReplySubmit}
                        disabled={isReplying || !replyContent.trim()}
                        className="px-4 py-2 bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-xl font-medium disabled:opacity-50 flex items-center gap-2"
                      >
                        {isReplying ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Actions Menu */}
          {isAuthor && (
            <div className="relative">
              <button
                onClick={() => setShowActions(!showActions)}
                className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <MoreVertical className="w-4 h-4 text-gray-500" />
              </button>
              <AnimatePresence>
                {showActions && (
                  <>
                    <div
                      className="fixed inset-0"
                      onClick={() => setShowActions(false)}
                    />
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      className="absolute right-0 mt-1 w-32 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-10"
                    >
                      <button
                        onClick={handleDelete}
                        className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg flex items-center gap-2"
                      >
                        <Trash2 className="w-3 h-3" />
                        Delete
                      </button>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>
          )}
        </div>
      </div>

      {/* Nested Replies */}
      {comment.replies && comment.replies.length > 0 && (
        <div className="space-y-4">
          {comment.replies.map((reply) => (
            <Comment
              key={reply.id}
              comment={reply}
              onReply={onReply}
              onLike={onLike}
              onDelete={onDelete}
              currentUser={currentUser}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </motion.div>
  );
};

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { scrollYProgress } = useScroll();
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const commentsEndRef = useRef(null);
  const { success: toastSuccess, error: toastError } = useToast();

  const [blog, setBlog] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [popularPosts, setPopularPosts] = useState([]);
  const [blogCategories, setBlogCategories] = useState([]);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isLiked, setIsLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [userReaction, setUserReaction] = useState(null);
  const [reactionCounts, setReactionCounts] = useState({});
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [commentsPage, setCommentsPage] = useState(1);
  const [commentsTotal, setCommentsTotal] = useState(0);
  const [commentsTotalPages, setCommentsTotalPages] = useState(1);
  const [showAllComments, setShowAllComments] = useState(false);
  const isMounted = useRef(true);

  const formatDate = (dateString) => {
    if (!dateString) return "Recent";
    try {
      const date =
        typeof dateString === "string"
          ? parseISO(dateString)
          : new Date(dateString);
      if (isValid(date)) return format(date, "MMMM dd, yyyy");
      return "Recent";
    } catch (error) {
      return "Recent";
    }
  };

  useEffect(() => {
    isMounted.current = true;
    window.scrollTo(0, 0);
    fetchBlogPost();
    fetchPopularPosts();

    return () => {
      isMounted.current = false;
    };
  }, [slug]);

  useEffect(() => {
    if (blog?.id && isMounted.current) {
      fetchComments();
    }
  }, [blog?.id, commentsPage]);

  const fetchBlogPost = async () => {
    if (!isMounted.current) return;

    setLoading(true);
    setError(null);
    try {
      const response = await blogService.getBlogBySlug(slug);

      if (!isMounted.current) return;

      let blogData;
      if (response?.data?.blog) blogData = response.data.blog;
      else if (response?.blog) blogData = response.blog;
      else if (response?.data) blogData = response.data;
      else blogData = response;

      setBlog(blogData);
      setLikesCount(blogData?.likes || 0);
      console.log("Post Data:", response);

      // AFTER setting blog, fetch categories
      if (isMounted.current) {
        await fetchBlogCategories(blogData);
      }

      if (blogData?.id) {
        try {
          const reactionResponse = await blogService.getReactionCounts(
            blogData.id,
          );
          if (isMounted.current) {
            setReactionCounts(reactionResponse?.data?.counts || {});
          }
        } catch (err) {
          // Silently fail for reactions
        }

        // Fetch related posts by category
        if (
          blogData.categoryId ||
          (blogData.categories && blogData.categories.length > 0)
        ) {
          try {
            const categoryId =
              blogData.categoryId || blogData.categories[0]?.id;
            const categorySlug = blogData.categories?.[0]?.slug;

            let relatedResponse;
            if (categorySlug) {
              relatedResponse = await blogService.getBlogsByCategorySlug(
                categorySlug,
                {
                  limit: 4,
                },
              );
            } else if (categoryId) {
              relatedResponse = await blogService.getBlogsByCategoryId(
                categoryId,
                { limit: 4 },
              );
            }

            if (isMounted.current && relatedResponse) {
              let relatedData = [];
              if (relatedResponse?.data?.data?.blogs)
                relatedData = relatedResponse.data.data.blogs;
              else if (relatedResponse?.data?.blogs)
                relatedData = relatedResponse.data.blogs;
              else if (relatedResponse?.blogs)
                relatedData = relatedResponse.blogs;
              else if (Array.isArray(relatedResponse?.data))
                relatedData = relatedResponse.data;
              else if (Array.isArray(relatedResponse))
                relatedData = relatedResponse;

              relatedData = relatedData
                .filter((post) => post.id !== blogData.id)
                .slice(0, 3);
              setRelatedPosts(relatedData);
            }
          } catch (err) {
            console.error("Error fetching related posts:", err);
            if (isMounted.current) setRelatedPosts([]);
          }
        }
      }

      const bookmarks = JSON.parse(
        localStorage.getItem("bookmarkedPosts") || "[]",
      );
      if (isMounted.current) {
        setIsBookmarked(bookmarks.includes(blogData?.id));
      }
    } catch (err) {
      if (isMounted.current) {
        console.error("Error fetching blog post:", err);
        setError("Failed to load blog post. Please try again.");
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  const fetchBlogCategories = async (blogData) => {
    if (!isMounted.current) return;

    try {
      console.log("Fetching categories...");
      const response = await blogService.getBlogCategories({ limit: 10 });
      console.log("Categories response:", response);

      if (!isMounted.current) return;

      let categoriesData = [];
      if (response?.data?.data) categoriesData = response.data.data;
      else if (response?.data) categoriesData = response.data;
      else if (Array.isArray(response)) categoriesData = response;
      else if (response?.categories) categoriesData = response.categories;

      if (categoriesData && categoriesData.length > 0) {
        const formattedCategories = categoriesData.slice(0, 6).map((cat) => ({
          id: cat.id,
          name: cat.name,
          slug: cat.slug,
          postCount: cat.postsCount || cat.postCount || cat._count?.posts || 0,
        }));
        setBlogCategories(formattedCategories);
      } else {
        // Fallback to blog tags
        if (blogData?.tags && blogData.tags.length > 0) {
          const fallbackCategories = blogData.tags
            .slice(0, 6)
            .map((tag, index) => ({
              id: index,
              name: tag.charAt(0).toUpperCase() + tag.slice(1),
              slug: tag.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
              postCount: 0,
            }));
          setBlogCategories(fallbackCategories);
        }
      }
    } catch (err) {
      console.error("Error fetching categories");
    }
  };

  const fetchPopularPosts = async () => {
    if (!isMounted.current) return;

    try {
      const response = await blogService.getPopularBlogs(5);
      if (!isMounted.current) return;

      let popularData = [];
      if (response?.data?.data) popularData = response.data.data;
      else if (response?.data) popularData = response.data;
      else if (Array.isArray(response)) popularData = response;
      setPopularPosts(popularData);
    } catch (err) {
      // Silently fail for popular posts
    }
  };

  const fetchComments = async () => {
    if (!blog?.id || !isMounted.current) return;

    try {
      const response = await blogService.getBlogComments(blog.id, {
        page: commentsPage,
        limit: 10,
        status: "approved",
      });

      if (!isMounted.current) return;

      let commentsList = [];
      let paginationData = { total: 0, pages: 1 };

      if (response?.data?.data?.comments) {
        commentsList = response.data.data.comments;
        paginationData = response.data.data.pagination || {
          total: commentsList.length,
          pages: 1,
        };
      } else if (response?.data?.comments) {
        commentsList = response.data.comments;
        paginationData = response.data.pagination || {
          total: commentsList.length,
          pages: 1,
        };
      } else if (response?.comments) {
        commentsList = response.comments;
        paginationData = response.pagination || {
          total: commentsList.length,
          pages: 1,
        };
      } else if (Array.isArray(response?.data)) {
        commentsList = response.data;
      } else if (Array.isArray(response)) {
        commentsList = response;
      }

      setComments(commentsList);
      setCommentsTotal(paginationData.total || commentsList.length);
      setCommentsTotalPages(paginationData.pages || 1);
    } catch (err) {
      console.error("Error fetching comments:", err);
      // Don't show toast for comments fetch error
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) {
      toastError("Please enter a comment");
      return;
    }
    if (!isAuthenticated) {
      toastError("Please login to comment");
      navigate("/user/login", { state: { from: window.location.pathname } });
      return;
    }

    setIsSubmittingComment(true);
    try {
      const userName =
        user?.firstName && user?.lastName
          ? `${user.firstName} ${user.lastName}`
          : user?.firstName || user?.email || "Anonymous";

      const userEmail = user?.email || null;

      await blogService.addComment(blog.id, newComment, userName, userEmail);

      if (!isMounted.current) return;

      setNewComment("");
      toastSuccess("Comment added! Waiting for approval.");
      await fetchComments();
      setTimeout(() => {
        commentsEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 500);
    } catch (err) {
      console.error("Error adding comment:", err);
      if (isMounted.current) {
        toastError(err.response?.data?.message || "Failed to add comment");
      }
    } finally {
      if (isMounted.current) {
        setIsSubmittingComment(false);
      }
    }
  };

  const handleLike = async () => {
    if (isLiked || !blog?.id) return;
    try {
      await blogService.toggleLike(blog.id);
      setIsLiked(true);
      setLikesCount((prev) => prev + 1);
      toastSuccess("Liked!");
    } catch (err) {
      toastError("Failed to like");
    }
  };

  const handleReaction = async (reactionType) => {
    if (!blog?.id) return;
    try {
      if (userReaction === reactionType) {
        await blogService.removeReaction(blog.id);
        setUserReaction(null);
        setReactionCounts((prev) => ({
          ...prev,
          [reactionType]: Math.max((prev[reactionType] || 0) - 1, 0),
        }));
        toastSuccess("Reaction removed");
      } else {
        if (userReaction) {
          setReactionCounts((prev) => ({
            ...prev,
            [userReaction]: Math.max((prev[userReaction] || 0) - 1, 0),
          }));
        }
        await blogService.setReaction(blog.id, reactionType);
        setUserReaction(reactionType);
        setReactionCounts((prev) => ({
          ...prev,
          [reactionType]: (prev[reactionType] || 0) + 1,
        }));
        toastSuccess("Reaction added!");
      }
    } catch (err) {
      toastError("Failed to add reaction");
    }
  };

  const handleShare = async (platform) => {
    const url = window.location.href;
    const title = blog?.title || "Check out this article";
    switch (platform) {
      case "facebook":
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
          "_blank",
        );
        break;
      case "twitter":
        window.open(
          `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`,
          "_blank",
        );
        break;
      case "linkedin":
        window.open(
          `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`,
          "_blank",
        );
        break;
      case "copy":
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        toastSuccess("Link copied!");
        break;
    }
    setShowShareMenu(false);
    if (blog?.id) {
      try {
        await blogService.shareBlog(blog.id);
      } catch (err) {}
    }
  };

  const handleBookmark = () => {
    if (!blog?.id) return;
    setIsBookmarked(!isBookmarked);
    const bookmarks = JSON.parse(
      localStorage.getItem("bookmarkedPosts") || "[]",
    );
    if (!isBookmarked) {
      bookmarks.push(blog.id);
      toastSuccess("Bookmarked!");
    } else {
      const index = bookmarks.indexOf(blog.id);
      if (index > -1) bookmarks.splice(index, 1);
      toastSuccess("Bookmark removed");
    }
    localStorage.setItem("bookmarkedPosts", JSON.stringify(bookmarks));
  };

  const handleAddReply = async (commentId, content) => {
    if (!isAuthenticated) {
      toastError("Please login to reply");
      navigate("/user/login", { state: { from: window.location.pathname } });
      return;
    }
    try {
      const userName =
        user?.firstName && user?.lastName
          ? `${user.firstName} ${user.lastName}`
          : user?.firstName || user?.email || "Anonymous";

      const userEmail = user?.email || null;

      await blogService.addReply(commentId, content, userName, userEmail);

      if (isMounted.current) {
        await fetchComments();
      }
    } catch (err) {
      console.error("Error adding reply:", err);
      if (isMounted.current) {
        toastError("Failed to add reply");
      }
      throw err;
    }
  };

  const handleLikeComment = async (commentId) => {
    try {
      await blogService.likeComment(commentId);
    } catch (err) {
      console.error("Error liking comment:", err);
      if (isMounted.current) {
        toastError("Failed to like comment");
      }
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await blogService.deleteComment(commentId);
      if (isMounted.current) {
        await fetchComments();
      }
    } catch (err) {
      console.error("Error deleting comment:", err);
      if (isMounted.current) {
        toastError("Failed to delete comment");
      }
    }
  };

  const reactionTypes = [
    { type: "like", icon: ThumbsUp, label: "Like" },
    { type: "love", icon: Heart, label: "Love" },
    { type: "clap", icon: Handshake, label: "Clap" },
    { type: "insightful", icon: Lightbulb, label: "Insightful" },
    { type: "helpful", icon: Smile, label: "Helpful" },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2
            size={48}
            className="animate-spin text-primary-500 mx-auto mb-4"
          />
          <p className="text-gray-600 dark:text-gray-400">Loading article...</p>
        </div>
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
            <AlertCircle className="w-10 h-10 text-red-500" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Article Not Found
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {error || "The article you're looking for doesn't exist."}
          </p>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white rounded-xl font-semibold"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 border border-gray-200 dark:border-gray-900 rounded-2xl shadow-lg">
      {/* Background Layers */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-[40%] -left-[20%] w-[80%] h-[80%] bg-gradient-to-br from-primary-200/20 to-secondary-200/20 dark:from-primary-900/20 dark:to-secondary-900/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute -bottom-[40%] -right-[20%] w-[80%] h-[80%] bg-gradient-to-tl from-primary-300/10 to-secondary-300/10 dark:from-primary-800/10 dark:to-secondary-800/10 rounded-full blur-3xl animate-pulse delay-1000" />
      </div>

      {/* Progress Bar */}
      <motion.div
        className="fixed top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary-500 to-secondary-500 origin-left z-50"
        style={{ scaleX: scrollYProgress }}
      />

      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl border-b border-white/30 dark:border-gray-800/30 rounded-t-2xl">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link
              to="/blog"
              className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-primary-600 transition-colors group"
            >
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
              <span className="text-sm font-medium">Back to Blog</span>
            </Link>
            <div className="flex items-center gap-2">
              <button
                onClick={handleBookmark}
                className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:text-primary-600 hover:bg-primary-50 transition-all"
              >
                <Bookmark
                  className={`w-4 h-4 ${isBookmarked ? "fill-primary-500 text-primary-500" : ""}`}
                />
              </button>
              <button
                onClick={() => window.print()}
                className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:text-primary-600 hover:bg-primary-50 transition-all"
              >
                <Printer className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >
              <div className="flex flex-wrap items-center gap-3 mb-6">
                {blog.categories && blog.categories.length > 0 && (
                  <Link
                    to={`/blog/category/${blog.categories[0].slug}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 rounded-full text-xs font-medium hover:bg-primary-200 dark:hover:bg-primary-900/50 transition-colors"
                  >
                    <FolderOpen className="w-3 h-3" />
                    {blog.categories[0].name}
                  </Link>
                )}
                {blog.isFeatured && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-full text-xs font-medium">
                    <Star className="w-3 h-3" />
                    Featured
                  </span>
                )}
                <div className="flex items-center gap-3 text-xs text-gray-500">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5" />
                    <span>
                      {formatDate(blog.publishedAt || blog.createdAt)}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" />
                    <span>{blog.readingTime || 5} min read</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Eye className="w-3.5 h-3.5" />
                    <span>{blog.views || 0} views</span>
                  </div>
                </div>
              </div>

              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-gray-900 dark:text-white mb-6 leading-tight">
                {blog.title}
              </h1>

              <Link
                to={`/blog/author/${blog.authorId}`}
                className="flex items-center gap-4 mb-8 p-4 bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl rounded-2xl border border-white/30 dark:border-gray-800/30 hover:bg-white/60 dark:hover:bg-gray-900/60 transition-all group"
              >
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center shadow-md group-hover:scale-105 transition-transform">
                  <User className="w-6 h-6 text-white" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900 dark:text-white group-hover:text-primary-600 transition-colors">
                    {blog.author || "TradeBridge Team"}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    Author • View all posts by this author
                  </div>
                </div>
                <ChevronRight className="ml-auto w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-all group-hover:translate-x-1" />
              </Link>

              {(blog.featuredImageBlob || blog.featuredImage) && (
                <div className="rounded-2xl overflow-hidden shadow-2xl mb-8">
                  <BlogImage blog={blog} className="w-full h-auto" />
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Article Content */}
            <article className="flex-1 min-w-0">
              <div className="bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl rounded-2xl border border-white/30 dark:border-gray-800/30 overflow-hidden">
                <div className="p-6 sm:p-8 lg:p-10">
                  {blog.excerpt && (
                    <div className="mb-8 pb-6 border-b border-gray-200 dark:border-gray-700">
                      <p className="text-lg text-gray-600 dark:text-gray-400 italic leading-relaxed">
                        {blog.excerpt}
                      </p>
                    </div>
                  )}
                  <div
                    className="prose prose-lg dark:prose-invert max-w-none prose-headings:font-bold prose-headings:text-gray-900 dark:prose-headings:text-white prose-h1:text-3xl prose-h2:text-2xl prose-h3:text-xl prose-p:text-gray-600 dark:prose-p:text-gray-300 prose-a:text-primary-600 prose-strong:text-gray-900 dark:prose-strong:text-white prose-img:rounded-xl prose-img:shadow-lg prose-blockquote:border-l-4 prose-blockquote:border-primary-500 prose-blockquote:bg-primary-50/50 dark:prose-blockquote:bg-primary-900/20 prose-blockquote:pl-4 prose-blockquote:italic"
                    dangerouslySetInnerHTML={{ __html: blog.content }}
                  />

                  {blog.tags && blog.tags.length > 0 && (
                    <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                      <div className="flex flex-wrap items-center gap-3">
                        <div className="flex items-center gap-1 text-sm text-gray-500">
                          <Tag className="w-4 h-4" />
                          <span>Tags:</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {blog.tags.map((tag, i) => (
                            <Link
                              key={i}
                              to={`/blog/tag/${encodeURIComponent(tag)}`}
                              className="px-3 py-1.5 text-xs bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded-full hover:bg-primary-100 hover:text-primary-600 transition-all"
                            >
                              #{tag}
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Actions Bar */}
                <div className="p-6 sm:p-8 bg-white/60 dark:bg-gray-900/60 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex flex-wrap items-center gap-4">
                      <button
                        onClick={handleLike}
                        disabled={isLiked}
                        className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all ${isLiked ? "bg-red-500 text-white" : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-red-50 hover:text-red-500"}`}
                      >
                        <Heart
                          className={`w-4 h-4 ${isLiked ? "fill-current" : ""}`}
                        />
                        <span className="font-medium">{likesCount}</span>
                      </button>
                      <div className="flex gap-1">
                        {reactionTypes.map(({ type, icon: Icon }) => (
                          <ReactionButton
                            key={type}
                            type={type}
                            icon={Icon}
                            count={reactionCounts[type] || 0}
                            isActive={userReaction === type}
                            onClick={() => handleReaction(type)}
                          />
                        ))}
                      </div>
                      <div className="relative">
                        <button
                          onClick={() => setShowShareMenu(!showShareMenu)}
                          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-primary-50 hover:text-primary-600 transition-all"
                        >
                          <Share2 className="w-4 h-4" />
                          <span className="font-medium">Share</span>
                        </button>
                        {showShareMenu && (
                          <div className="absolute bottom-full left-0 mb-2 p-2 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 min-w-[160px] z-50">
                            <button
                              onClick={() => handleShare("facebook")}
                              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 rounded-lg"
                            >
                              <Facebook className="w-4 h-4 text-blue-600" />
                              Facebook
                            </button>
                            <button
                              onClick={() => handleShare("twitter")}
                              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 rounded-lg"
                            >
                              <Twitter className="w-4 h-4 text-blue-400" />
                              Twitter
                            </button>
                            <button
                              onClick={() => handleShare("linkedin")}
                              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 rounded-lg"
                            >
                              <Linkedin className="w-4 h-4 text-blue-700" />
                              LinkedIn
                            </button>
                            <button
                              onClick={() => handleShare("copy")}
                              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 rounded-lg"
                            >
                              {copied ? (
                                <Check className="w-4 h-4 text-green-500" />
                              ) : (
                                <LinkIcon className="w-4 h-4" />
                              )}
                              {copied ? "Copied!" : "Copy Link"}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    <Link
                      to="/contact"
                      className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-xl font-medium hover:shadow-lg transition-all"
                    >
                      <MessageCircle className="w-4 h-4" />
                      Discuss this article
                    </Link>
                  </div>
                </div>
              </div>

              {/* Comments Section - Remaining code stays the same */}
              <div className="mt-8">
                <div className="bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl rounded-2xl border border-white/30 dark:border-gray-800/30 overflow-hidden">
                  <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <MessageSquare className="w-5 h-5 text-primary-500" />
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                          Comments ({commentsTotal})
                        </h3>
                      </div>
                    </div>
                  </div>

                  {/* Add Comment Form */}
                  <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex gap-4">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center shadow-md flex-shrink-0">
                        <span className="text-sm font-bold text-white">
                          {user?.firstName?.charAt(0) || "G"}
                        </span>
                      </div>
                      <div className="flex-1">
                        <textarea
                          value={newComment}
                          onChange={(e) => setNewComment(e.target.value)}
                          placeholder={
                            isAuthenticated
                              ? "Share your thoughts..."
                              : "Please login to comment..."
                          }
                          rows={3}
                          disabled={!isAuthenticated}
                          className="w-full px-4 py-3 bg-white/50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all resize-none"
                        />
                        <div className="flex justify-end mt-3">
                          <button
                            onClick={handleAddComment}
                            disabled={
                              !isAuthenticated ||
                              !newComment.trim() ||
                              isSubmittingComment
                            }
                            className="flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-xl font-medium disabled:opacity-50 transition-all hover:shadow-lg"
                          >
                            {isSubmittingComment ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Send className="w-4 h-4" />
                            )}
                            Post Comment
                          </button>
                        </div>
                        {!isAuthenticated && (
                          <p className="text-xs text-gray-500 mt-2">
                            <Link
                              to="/user/login"
                              className="text-primary-600 hover:underline"
                            >
                              Login
                            </Link>{" "}
                            to join the conversation
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Comments List */}
                  <div className="p-6">
                    {comments.length === 0 ? (
                      <div className="text-center py-8">
                        <MessageCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                        <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
                          No comments yet
                        </h4>
                        <p className="text-sm text-gray-500">
                          Be the first to share your thoughts!
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {(showAllComments
                          ? comments
                          : comments.slice(0, 5)
                        ).map((comment) => (
                          <Comment
                            key={comment.id}
                            comment={comment}
                            onReply={handleAddReply}
                            onLike={handleLikeComment}
                            onDelete={handleDeleteComment}
                            currentUser={user}
                          />
                        ))}
                      </div>
                    )}
                    {commentsTotal > 5 && !showAllComments && (
                      <div className="text-center mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <button
                          onClick={() => setShowAllComments(true)}
                          className="text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1 mx-auto"
                        >
                          Load all {commentsTotal} comments
                          <ChevronDown className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                    <div ref={commentsEndRef} />
                  </div>
                </div>
              </div>

              {/* Related Posts */}
              {relatedPosts.length > 0 && (
                <div className="mt-8">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-primary-500" />
                    Related Articles
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {relatedPosts.map((post) => (
                      <Link
                        key={post.id}
                        to={`/blog/${post.slug}`}
                        className="group bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl rounded-xl overflow-hidden border border-white/30 dark:border-gray-800/30 hover:shadow-lg transition-all"
                      >
                        <div className="aspect-[16/9] overflow-hidden">
                          <BlogImage
                            blog={post}
                            className="w-full h-full group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                        <div className="p-4">
                          <h4 className="font-semibold text-gray-900 dark:text-white line-clamp-2 group-hover:text-primary-600 transition-colors">
                            {post.title}
                          </h4>
                          <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                            <Calendar className="w-3 h-3" />
                            <span>
                              {formatDate(post.publishedAt || post.createdAt)}
                            </span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </article>

            {/* Sidebar */}
            <aside className="lg:w-80 flex-shrink-0 space-y-6">
              {/* Top Categories Section */}
              {blogCategories && blogCategories.length > 0 && (
                <div className="bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl rounded-2xl p-6 border border-white/30 dark:border-gray-800/30">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
                      <FolderOpen className="w-4 h-4 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Top Categories
                    </h3>
                  </div>
                  <div className="space-y-3">
                    {blogCategories.slice(0, 5).map((category, index) => (
                      <Link
                        key={category.id || category.slug}
                        to={`/blog/category/${category.slug}`}
                        className="flex items-center justify-between group p-2 rounded-lg hover:bg-white/50 transition-all"
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-6 h-6 rounded-md bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center flex-shrink-0 transition-all group-hover:scale-110`}
                          >
                            <FolderOpen className="w-3 h-3 text-white" />
                          </div>
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-primary-600 transition-colors">
                            {category.name}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 bg-white/50 dark:bg-gray-800/50 px-2 py-0.5 rounded-full">
                            {category.postCount || category.count || 0}
                          </span>
                          <ChevronRight className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-all group-hover:translate-x-1" />
                        </div>
                      </Link>
                    ))}
                  </div>
                  {blogCategories.length > 5 && (
                    <Link
                      to="/blog/categories"
                      className="mt-3 text-xs text-primary-600 hover:text-primary-700 font-medium flex items-center justify-center gap-1"
                    >
                      View all categories
                      <ChevronRight className="w-3 h-3" />
                    </Link>
                  )}
                </div>
              )}

              {/* Most Popular Section */}
              {popularPosts.length > 0 && (
                <div className="bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl rounded-2xl p-6 border border-white/30 dark:border-gray-800/30">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
                      <Flame className="w-4 h-4 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Most Popular
                    </h3>
                  </div>
                  <div className="space-y-3">
                    {popularPosts.slice(0, 5).map((post, i) => (
                      <Link
                        key={post.id}
                        to={`/blog/${post.slug}`}
                        className="flex items-start gap-3 group p-2 rounded-lg hover:bg-white/50 transition-all"
                      >
                        <div className="w-6 h-6 rounded-md bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-bold text-white">
                            {i + 1}
                          </span>
                        </div>
                        <div className="flex-1">
                          <h4 className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-primary-600 transition-colors line-clamp-2">
                            {post.title}
                          </h4>
                          <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                            <Eye className="w-3 h-3" />
                            <span>{post.views || 0} views</span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Newsletter Section */}
              <div className="bg-gradient-to-br from-primary-500/10 to-secondary-500/10 backdrop-blur-xl rounded-2xl p-6 border border-primary-200/30 dark:border-primary-800/30">
                <div className="text-center">
                  <BookOpen className="w-10 h-10 text-primary-500 mx-auto mb-3" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    Weekly Newsletter
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Get the latest articles delivered to your inbox
                  </p>
                  <input
                    type="email"
                    placeholder="Your email address"
                    className="w-full px-3 py-2 mb-3 bg-white/60 dark:bg-gray-900/60 border border-white/30 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <button className="w-full px-4 py-2 bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-lg text-sm font-medium">
                    Subscribe
                  </button>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPost;
